import { ToastProps, ToastType as ToastWidgetType, ToastWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';

export type ToastId = number;

export interface IToast extends Pick<ToastProps, 'actionHref' | 'actionMessage' | 'children'> {
  action?: () => void;
  children?: React.ReactNode;
  /**
  * @deprecated: This is never used
  */
  data?: any;
  id?: ToastId;
  /**
  * @deprecated: This is never updated
  */
  isClosed: boolean;
  keepOnLocationChange: boolean;
  message?: string;
  testId?: string;
  /**
   * @deprecated: Use message instead
   */
  text?: string;
  timeoutDelay: number;
  type: string;
}

interface IToastWidget extends IToast {
  /** internally used to track the id assigned by the ToastWidget */
  toastWidgetId: string;
}

export enum ToastType {
  Success,
  Info,
  Error,
  External,
  InfoNoFadeout,
  Warning
}

export interface IToastOptions extends Pick<ToastProps, 'actionHref' | 'actionMessage' | 'children'> {
  action?: () => void;
  data?: any;
  keepOnLocationChange?: boolean;
  message?: string;
  testId?: string;
  text?: string;
  timeoutDelay?: number;
  type: ToastType;
}

export class ToasterService {
  public static readonly instance = new ToasterService();

  public static NoTimeout = 0;

  private static DefaultSeconds: number = 5;
  private static Milliseconds: number = 1000;

  private nextToastId: number = 0;

  public toasts: IToastWidget[];

  constructor() {
    this.toasts = [];
  }

  private getToastPropsFromIToast(iToast: IToast): ToastProps {
    const autoDismissTimeoutUserPref = (SessionUserSelectors.toastAutoDismissTimeout(getAppStore().getState()) ?? ToasterService.DefaultSeconds) * 1000;
    const shouldAutoDismiss = autoDismissTimeoutUserPref !== 0 && iToast.timeoutDelay !== 0;
    return {
      actionMessage: iToast.actionMessage,
      actionHref: iToast.actionHref || 'javascript:void(0)',
      autoDismiss: iToast.type !== 'infonofadeout' && shouldAutoDismiss,
      // use whichever is longer between `iToast.timeoutDelay` or the user preference. `iToast.timeoutDelay` would mean that there is a feature-specific reason for having a long-lasting toast
      ...(shouldAutoDismiss && {autoDismissTimeMS: isNaN(iToast.timeoutDelay) ? autoDismissTimeoutUserPref : Math.max(autoDismissTimeoutUserPref, iToast.timeoutDelay)}),
      children: iToast.children,
      message: iToast.message || iToast.text,
      onAction: iToast.action,
      onCloseRequest: () => this.close(iToast.id),
      testId: iToast.testId,
      toastType: this.convertToastType(iToast.type),
    };
  }

  private convertToastType(type: string): ToastWidgetType {
    switch (type) {
      case 'info':
      case 'infonofadeout':
        return ToastWidgetType.Info;
      case 'error':
        return ToastWidgetType.Error;
      case 'warning':
        return ToastWidgetType.Warning;
      // this commented out type existed on the old toast, but doesn't exist on shared widgets.
      // case 'external':
      default:
        return ToastWidgetType.Success;
    }
  }

  public pop(toastOptions: IToastOptions): number {
    const toast: IToast = {
      action: toastOptions.action,
      actionHref: toastOptions.actionHref,
      actionMessage: toastOptions.actionMessage,
      children: toastOptions.children,
      data: toastOptions.data,
      id: this.nextToastId ++,
      isClosed: false,
      keepOnLocationChange: toastOptions.keepOnLocationChange || false, // in run.ts we dismiss all open toasts unless this is true
      message: toastOptions.message || toastOptions.text,
      testId: toastOptions.testId || 'tb-toast',
      text: toastOptions.text,
      timeoutDelay: toastOptions.timeoutDelay,
      type: ToastType[toastOptions.type].toLowerCase(),
    };
    toast.timeoutDelay = _.isNumber(toastOptions.timeoutDelay)
    ? toastOptions.timeoutDelay
      : undefined;

    const toastProps: ToastProps = this.getToastPropsFromIToast(toast);
    const toastWidgetId = ToastWidget.show(toastProps);
    this.toasts.push({
      ...toast,
      toastWidgetId,
    });

    return toast.id;
  }


  public close(id: number) {
    const toastIndexToClose = _.findIndex(this.toasts, (toast: IToastWidget) => toast.id === id);

    if (toastIndexToClose === -1) { return; }

    const toastWidgetId = this.toasts[toastIndexToClose].toastWidgetId;
    this.toasts.splice(toastIndexToClose, 1);
    ToastWidget.dismiss(toastWidgetId);
  }

  public closeAllToastsNotMarkedAsKeep() {
    const toastsToKeep: IToastWidget[] = [];
    // Since this.toasts is mutated by its close method, iterate over a copy
    this.toasts.slice().forEach((toast: IToastWidget) => {
      if (toast.keepOnLocationChange) {
        toastsToKeep.push(toast);
      } else {
        this.close(toast.id);
      }
    });
    this.toasts = toastsToKeep;
  }

  public markToastsAsDoNotDismissOnLocationChange(): void {
    this.toasts.forEach(toast => {
      toast.keepOnLocationChange = true;
    });
  }

  public static getInstance() {
    return ToasterService.instance;
  }
}
